/**
 * VTCT course assessment methods (editorial source of truth).
 * Populated course-by-course with the team; used for the
 * "How is the course assessed?" accordion on course pages.
 *
 * Do not derive assessment copy from vtctSettings.dates.
 */

export const COURSE_ASSESSMENT_METHODS = [
  "practical_observations",
  "short_answer_questions",
  "short_answer_question_documents",
  "online_theory_exams",
  "online_theory_exam",
  "online_written_assignment",
  "theory_assignments",
  "written_assignments",
  "end_point_assessment",
  "end_point_assessment_live_models",
  "formative_case_studies",
  "formative_assessments",
  "summative_assessments",
  "summative_independent_assessments",
] as const;

export type CourseAssessmentMethod =
  (typeof COURSE_ASSESSMENT_METHODS)[number];

export const COURSE_ASSESSMENT_METHOD_LABELS: Record<
  CourseAssessmentMethod,
  string
> = {
  practical_observations: "Practical observations",
  short_answer_questions: "Short Answer Questions (written assignments)",
  short_answer_question_documents: "Short answer question documents",
  online_theory_exams: "Online theory exams",
  online_theory_exam: "Online theory exam",
  online_written_assignment: "Online written assignment",
  theory_assignments: "Theory assignments",
  written_assignments: "Written assignments",
  end_point_assessment: "End point assessment with VTCT assessor",
  end_point_assessment_live_models: "End point assessment with a VTCT assessor",
  formative_case_studies: "Formative case studies",
  formative_assessments: "Formative assessments (working with tutors/assessors)",
  summative_assessments:
    "Summative assessments (fully independent under assessor observation)",
  summative_independent_assessments: "Summative independent assessments",
};

export const COURSE_ASSESSMENT_METHOD_DESCRIPTIONS: Record<
  CourseAssessmentMethod,
  string
> = {
  practical_observations:
    "Your tutor observes you carrying out treatments during training to confirm you meet the required standards.",
  short_answer_questions:
    "Written assignments set by VTCT that check your underpinning theory and professional knowledge.",
  short_answer_question_documents:
    "Set by VTCT to check your underpinning theory and professional knowledge.",
  online_theory_exams:
    "Remote online examinations covering the theory units for this qualification.",
  online_theory_exam:
    "A remote online examination covering the theory units for this qualification.",
  online_written_assignment:
    "Once you have completed the e-learning, an online written assignment is released for you to complete and submit.",
  theory_assignments:
    "Complete three theory assignments through our online portal.",
  written_assignments:
    "Completed during pre-study and due before your practical days.",
  end_point_assessment:
    "A final assessed practical with a VTCT assessor to confirm competence at the end of the programme.",
  end_point_assessment_live_models:
    "A final assessed practical on live models with a VTCT assessor, on your end point assessment day, to confirm competence at the end of the programme.",
  formative_case_studies:
    "Case studies completed during training, usually with tutor guidance, to build competence before final assessment.",
  formative_assessments:
    "Practice assessments with tutors or assessors that prepare you for independent, summative assessment.",
  summative_assessments:
    "Final practical assessments completed fully independently under assessor observation.",
  summative_independent_assessments:
    "Final practical assessments completed fully independently under assessor observation.",
};

/** Accordion titles we replace with structured assessment content. */
export const COURSE_ASSESSMENT_ACCORDION_TITLES = [
  "how is the course assessed?",
  "assessment & delivery",
  "assessment methods",
] as const;

export function formatAssessmentMethodLabel(
  method: CourseAssessmentMethod,
  count?: number,
  labelOverride?: string,
): string {
  const label = labelOverride || COURSE_ASSESSMENT_METHOD_LABELS[method];
  if (!labelOverride && typeof count === "number" && count > 0) {
    return `${label} (${count})`;
  }
  return label;
}

export function isAssessmentAccordionTitle(title?: string | null): boolean {
  if (!title) {
    return false;
  }
  return COURSE_ASSESSMENT_ACCORDION_TITLES.includes(
    title.trim().toLowerCase() as (typeof COURSE_ASSESSMENT_ACCORDION_TITLES)[number],
  );
}

export type CourseAssessmentConfig = {
  /** Content entry id for the course page */
  courseId: string;
  url: string;
  title: string;
  /**
   * Direct methods for standalone courses. For packages that inherit from
   * included courses, leave empty and set packageCourseIds instead.
   */
  methods: CourseAssessmentMethod[];
  /** Optional counts / clarifications, e.g. { online_theory_exams: 2 } */
  counts?: Partial<Record<CourseAssessmentMethod, number>>;
  /** Optional page-scoped label overrides (e.g. singular "Online theory exam"). */
  labels?: Partial<Record<CourseAssessmentMethod, string>>;
  /** Optional page-scoped description overrides for assessment methods. */
  descriptions?: Partial<Record<CourseAssessmentMethod, string>>;
  notes?: string;
  /**
   * When false, skip injecting this assessment block into the FAQ accordion
   * and FAQPage schema. Default true.
   */
  includeInFaq?: boolean;
  /**
   * Package pages: resolve assessment methods from these course entry ids
   * (union of each included course's methods).
   */
  packageCourseIds?: string[];
};

export const courseAssessments: CourseAssessmentConfig[] = [
  {
    courseId: "6VTg0QoePY3USAUaphKflM",
    url: "/courses/vtct/level-2-facial-and-skincare-course",
    title: "VTCT Level 2 Facial and Skincare Course",
    methods: ["online_theory_exams", "practical_observations"],
    counts: { online_theory_exams: 2 },
  },
  {
    courseId: "1MZYMjEqXeW5O4iMqAC0Dm",
    url: "/courses/vtct/vtct-level-2-certificate-in-womens-hairdressing",
    title: "VTCT Level 2 Certificate in Women's Hairdressing",
    methods: ["online_theory_exams", "practical_observations"],
  },
  {
    courseId: "5yvq5HxADjTDihwhE3fHsM",
    url: "/courses/vtct/level-3-award-in-basic-life-support-aed-and-management-of-anaphylaxis",
    title:
      "VTCT Level 3 Award in Adult Basic Life Support, AED and Management of Anaphylaxis",
    // No methods: the page already carries a hand-authored "How You'll Be
    // Assessed" accordion item, so the generic auto-injected assessment item
    // (previously copied from a treatment course) is suppressed here.
    methods: [],
    includeInFaq: false,
  },
  {
    courseId: "ddaROX5ghRzo6Ze3RDa18",
    url: "/courses/vtct/level-3-award-in-body-massage",
    title: "VTCT (ITEC) Level 3 Award in Body Massage",
    methods: [
      "online_theory_exams",
      "formative_case_studies",
      "end_point_assessment",
    ],
  },
  {
    courseId: "7fmcA3DWfsnVtZKeyvZfRf",
    url: "/courses/vtct/level-3-award-in-indian-head-massage",
    title: "VTCT (ITEC) Level 3 Award in Indian Head Massage",
    methods: [
      "online_theory_exams",
      "formative_case_studies",
      "end_point_assessment",
    ],
  },
  {
    courseId: "4XuhUXziRoBmlVFPY8rap9",
    url: "/courses/vtct/level-3-award-in-massage-using-pre-blended-aromatherapy-oils",
    title:
      "VTCT (ITEC) Level 3 Award in Massage using Pre-Blended Aromatherapy Oils",
    methods: [
      "online_theory_exams",
      "formative_case_studies",
      "end_point_assessment",
    ],
  },
  {
    courseId: "1graQlnkDnOBSl8sHkdf2L",
    url: "/courses/vtct/level-3-certificate-in-access-to-aesthetic-therapies",
    title: "VTCT (ITEC) Level 3 Certificate in Access to Aesthetic Therapies",
    methods: ["online_theory_exams", "practical_observations"],
    counts: { online_theory_exams: 2 },
  },
  {
    courseId: "5WlntKHxi5gIPcIG2CfTPJ",
    url: "/courses/vtct/vtct-level-3-award-in-education-and-training",
    title: "VTCT Level 3 Award in Education and Training",
    methods: ["practical_observations", "written_assignments"],
    labels: {
      practical_observations: "Observed micro-teach",
    },
    descriptions: {
      practical_observations:
        "Your tutor observes you plan and deliver a short teaching session to confirm you meet the required standards.",
      written_assignments:
        "Unit assignments you complete on our e-learning platform and submit before your onsite training session, as part of your portfolio of evidence.",
    },
    includeInFaq: false,
  },
  {
    courseId: "5zwAvgNpYGhfnP1tKGlj7J",
    url: "/courses/vtct/vtct-level-3-certificate-in-assessing-vocational-achievement",
    title: "VTCT Level 3 Certificate in Assessing Vocational Achievement",
    methods: ["practical_observations", "written_assignments"],
    labels: {
      practical_observations: "Observed assessments",
    },
    descriptions: {
      practical_observations:
        "Your assessor observes you carrying out assessments, through a mix of simulation and real learners, to confirm you meet the required standards.",
      written_assignments:
        "Assignments on the principles and practices of assessment, completed on our e-learning platform as part of your portfolio of evidence.",
    },
    includeInFaq: false,
  },
  {
    courseId: "5MrEz8xx8s0YFtNHRCes3i",
    url: "/courses/vtct/laser-hair-removal",
    title: "VTCT Level 4 Laser Hair Removal Training Course",
    methods: ["practical_observations", "short_answer_questions"],
  },
  {
    courseId: "4bH4KENdeJVgfdZ7iNEwJg",
    url: "/courses/vtct/level-4-award-in-dermaplaning",
    title: "VTCT Level 4 Award In Dermaplaning",
    methods: [
      "formative_case_studies",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
      end_point_assessment: "End point assessment with a VTCT assessor",
    },
    descriptions: {
      formative_case_studies:
        "6 case studies completed onsite during training, with tutor guidance, to build competence before final assessment.",
      online_theory_exams:
        "A remote online examination covering the theory for this qualification.",
    },
    includeInFaq: false,
  },
  {
    courseId: "6thZWz9oi1C1EJTWuoRVi1",
    url: "/courses/vtct/level-4-award-in-high-intensity-focused-ultrasound-hifu",
    title: "VTCT Level 4 Award in High Intensity Focused Ultrasound (HIFU)",
    methods: [
      "formative_case_studies",
      "online_written_assignment",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_written_assignment: "Written assignments",
      online_theory_exams: "Online theory exam",
      end_point_assessment: "End point assessment with a VTCT assessor",
    },
    descriptions: {
      formative_case_studies:
        "6 case studies completed onsite during training, with tutor guidance, to build competence before final assessment.",
      online_written_assignment:
        "Completed through your e-learning as part of your portfolio of evidence.",
      online_theory_exams:
        "A remote online examination covering the theory for this qualification.",
    },
    includeInFaq: false,
  },
  {
    courseId: "TRdRVDJ2Y149pLV3z7IYe",
    url: "/courses/vtct/level-4-award-in-skin-peeling",
    title: "VTCT Level 4 In Skin Peeling",
    methods: [
      "formative_case_studies",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
      end_point_assessment: "End point assessment with a VTCT assessor",
    },
    descriptions: {
      formative_case_studies:
        "Case studies completed during your 2 formative practical training days, with tutor guidance, to build competence before final assessment.",
      online_theory_exams:
        "A remote online examination covering the theory for this qualification.",
    },
    includeInFaq: false,
  },
  {
    courseId: "4k0m9rd1yFrd1xxulFQ62R",
    url: "/courses/vtct/level-4-award-in-the-internal-quality-assurance-of-assessment-processes",
    title:
      "VTCT Level 4 Award in the Internal Quality Assurance of Assessment Processes and Practice",
    methods: ["practical_observations", "short_answer_questions"],
    labels: {
      practical_observations: "Observed quality assurance practice",
    },
    descriptions: {
      practical_observations:
        "Your tutor observes you carrying out internal quality assurance, quality assuring assessors' work through a mixed approach, to confirm you meet the required standards.",
      short_answer_questions:
        "Written assignments on the principles and practices of internal quality assurance, completed as part of your portfolio of evidence.",
    },
    includeInFaq: false,
  },
  {
    courseId: "4eE28e2XRAIhmKXO5UJGi",
    url: "/courses/vtct/level-4-certificate-in-advanced-aesthetic-therapies-for-skin",
    title:
      "VTCT Level 4 Certificate in Advanced Aesthetic Therapies for Skin Rejuvenation",
    methods: [
      "practical_observations",
      "short_answer_questions",
      "formative_case_studies",
      "online_theory_exams",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
    },
    notes:
      "Live webinars: 2 days of online webinar with your tutor, alongside your e-learning.",
    includeInFaq: false,
  },
  {
    courseId: "2D6Xd7ARoNFR8qaqRl7FM2",
    url: "/courses/vtct/level-4-certificate-in-enhancing-eyebrows-with-microblading-techniques",
    title:
      "VTCT Level 4 Certificate in Enhancing Eyebrows with Microblading Techniques",
    methods: [
      "formative_case_studies",
      "short_answer_questions",
      "online_theory_exams",
      "summative_assessments",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
    },
    descriptions: {
      formative_case_studies:
        "5 independent case studies, completed on your own models after your practical training, to build competence before final assessment.",
      online_theory_exams:
        "A remote online multiple choice exam for the anatomy and physiology unit.",
    },
    includeInFaq: false,
  },
  {
    courseId: "3C2fRaQRiwMjA3Inq6UHqK",
    url: "/courses/vtct/level-4-certificate-in-micropigmentation",
    title: "VTCT Level 4 Certificate in Micropigmentation",
    methods: ["practical_observations", "short_answer_questions"],
    includeInFaq: false,
  },
  {
    courseId: "7m6zk4tuCFu3awTGAhhKfr",
    url: "/courses/vtct/level-4-in-skin-microneedling",
    title: "VTCT Level 4 In Skin Microneedling",
    methods: [
      "formative_case_studies",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
      end_point_assessment: "End point assessment with a VTCT assessor",
    },
    descriptions: {
      formative_case_studies:
        "6 case studies completed onsite during training, with tutor guidance, to build competence before final assessment.",
      online_theory_exams:
        "A remote online examination covering the theory for this qualification.",
    },
    includeInFaq: false,
  },
  {
    courseId: "3g1uBeizk5tdoTfMCd9Zws",
    url: "/courses/vtct/level-4-radio-frequency-and-ultrasound",
    title: "VTCT Level 4 Certificates in Radio Frequency & Ultrasound",
    methods: [
      "formative_case_studies",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
      end_point_assessment: "End point assessment with a VTCT assessor",
    },
    descriptions: {
      formative_case_studies:
        "6 case studies completed onsite during training, with tutor guidance, to build competence before final assessment.",
      online_theory_exams:
        "A remote online examination covering the theory for these qualifications.",
      end_point_assessment:
        "Final assessed practicals in radio frequency and in ultrasound with a VTCT assessor, on your end point assessment day, to confirm competence at the end of the programme.",
    },
    includeInFaq: false,
  },
  {
    courseId: "7KkGHh8CFL5fvPMGE0DtDT",
    url: "/courses/vtct/vtct-level-4-plasma-pen-training",
    title: "VTCT Level 4 Plasma Pen Training",
    methods: [
      "formative_case_studies",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
      end_point_assessment: "End point assessment with a VTCT assessor",
    },
    descriptions: {
      formative_case_studies:
        "6 case studies completed onsite during training, with tutor guidance, to build competence before final assessment.",
      online_theory_exams:
        "A remote online examination covering the theory for this qualification.",
    },
    includeInFaq: false,
  },
  {
    courseId: "3XxcCx5A6y3gXUqw8e0zxZ",
    url: "/courses/vtct/level-5-award-in-radiofrequency-microneedling",
    title: "VTCT Level 5 Award in Radiofrequency Microneedling",
    methods: [
      "formative_case_studies",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
    },
    descriptions: {
      online_theory_exams:
        "A remote online examination covering the theory for this qualification, booked at a time that suits you, so it is not a class day.",
    },
    includeInFaq: false,
  },
  {
    courseId: "6vB1wp7JL0TWzX46zfK0qw",
    url: "/courses/vtct/level-5-certificate-in-laser-tattoo-removal",
    title: "VTCT Level 5 Certificate in Laser Tattoo Removal",
    methods: [
      "theory_assignments",
      "formative_case_studies",
      "online_theory_exam",
      "end_point_assessment_live_models",
    ],
    includeInFaq: false,
  },
  {
    courseId: "4LRrhhrfoDMXiu7pOORPov",
    url: "/courses/vtct/level-5-certificate-in-non-surgical-aesthetic-procedures-for-skin",
    title:
      "VTCT Level 5 Certificate in Non-surgical Aesthetic Procedures for Skin Rejuvenation",
    methods: [
      "formative_case_studies",
      "summative_independent_assessments",
      "online_theory_exams",
      "written_assignments",
      "short_answer_question_documents",
    ],
    includeInFaq: false,
  },
  {
    courseId: "1wEt2OjxkbXXk4zbkIHUzt",
    url: "/courses/vtct/level-5-chemical-skin-peels",
    title: "VTCT Level 5 Award in Skin Peeling Treatments",
    methods: [
      "formative_case_studies",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
    },
    descriptions: {
      formative_case_studies:
        "6 case studies completed onsite during training, usually with tutor guidance, to build competence before final assessment.",
      online_theory_exams:
        "A remote online examination covering the theory for this qualification, booked at a time that suits you, so it is not a class day.",
    },
    includeInFaq: false,
  },
  {
    courseId: "spkajlg8oKCuyln5MyZvU",
    url: "/courses/vtct/vtct-level-5-diploma-in-education-and-training",
    title: "VTCT Level 5 Diploma in Education and Training",
    methods: ["short_answer_questions", "practical_observations"],
  },
  {
    courseId: "1adDAB6gGMKJyvw1FMYslh",
    url: "/courses/vtct/level-6-award-in-mesotherapy-treatments",
    title: "VTCT Level 6 Award in Mesotherapy Treatments",
    methods: [
      "formative_case_studies",
      "online_theory_exams",
      "end_point_assessment",
    ],
    labels: {
      online_theory_exams: "Online theory exam",
    },
    descriptions: {
      online_theory_exams:
        "A remote online examination covering the theory for this qualification, booked at a time that suits you, so it is not a class day.",
    },
    includeInFaq: false,
  },
  {
    courseId: "Jecsoipo4pD2CaquRABFAp",
    url: "/courses/vtct/level-7-certificate-in-bio-stimulatory-injectable-treatments",
    title:
      "VTCT Skills (ITEC) Level 7 Certificate in Bio-Stimulatory Injectable Treatments",
    methods: [
      "practical_observations",
      "formative_case_studies",
      "online_theory_exams",
      "short_answer_questions",
      "summative_assessments",
    ],
  },
  {
    courseId: "2bTpTXMNUYtXK6AX5AwkbX",
    url: "/courses/vtct/level-7-certificate-in-non-surgical-aesthetic-injectable-procedures",
    title:
      "VTCT Level 7 Certificate in Non-surgical Aesthetic Injectable Procedures",
    methods: [
      "practical_observations",
      "formative_case_studies",
      "online_theory_exams",
      "short_answer_questions",
      "summative_assessments",
    ],
  },
  {
    courseId: "Q7713i4aNx331gKeQRvp57",
    url: "/courses/vtct/level-7-diploma-in-clinical-aesthetic-injectable-treatments",
    title:
      "VTCT Skills (ITEC) Level 7 Diploma in Clinical Aesthetic Injectable Treatments",
    methods: [
      "practical_observations",
      "formative_case_studies",
      "online_theory_exams",
      "short_answer_questions",
      "summative_assessments",
    ],
  },
  {
    courseId: "2Scklnr73aCDKTZ9nhfT7R",
    url: "/courses/vtct/level-7-diploma-in-non-surgical-aesthetic-injectable-procedures",
    title:
      "VTCT Level 7 Diploma in Non-surgical Aesthetic Injectable Procedures",
    methods: [
      "practical_observations",
      "formative_case_studies",
      "online_theory_exams",
      "short_answer_questions",
      "summative_assessments",
    ],
  },
  {
    courseId: "4haRIzOe5kgVPEtmS7aodq",
    url: "/courses/vtct/level-7-diploma-in-non-surgical-cosmetic-injectable-treatments",
    title:
      "VTCT Skills (ITEC) Level 7 Diploma in Non-Surgical Cosmetic Injectable Treatments",
    methods: [
      "practical_observations",
      "formative_case_studies",
      "online_theory_exams",
      "short_answer_questions",
      "summative_assessments",
    ],
  },
  {
    courseId: "3itrSeuXeUM7ZnXb1osmtR",
    url: "/courses/education/vtct-teaching-and-assessing-package",
    title: "VTCT Teaching & Assessing Package",
    methods: ["practical_observations", "short_answer_questions"],
  },
  {
    courseId: "44HB9sV8oHmT2FNYU56nBS",
    url: "/courses/packages/vtct-itec-aesthetics-fast-track-package",
    title: "VTCT/ITEC Advanced Aesthetics Fast-Track Package",
    methods: [],
    packageCourseIds: [
      "1graQlnkDnOBSl8sHkdf2L", // Access to Aesthetic Therapies
      "4eE28e2XRAIhmKXO5UJGi", // Advanced Aesthetic Therapies for Skin
      "5MrEz8xx8s0YFtNHRCes3i", // Laser Hair Removal
    ],
    notes:
      "Assessment follows each qualification included in the package.",
  },
  {
    courseId: "F7oaVz52WIUDAPRx23dMfs",
    url: "/courses/packages/advanced-aesthetics-career-package",
    title: "Advanced Aesthetics Career Package",
    methods: [],
    packageCourseIds: [
      "1graQlnkDnOBSl8sHkdf2L", // Access to Aesthetic Therapies
      "4eE28e2XRAIhmKXO5UJGi", // Advanced Aesthetic Therapies for Skin
      "5MrEz8xx8s0YFtNHRCes3i", // Laser Hair Removal
      "4bH4KENdeJVgfdZ7iNEwJg", // Dermaplaning
      "6thZWz9oi1C1EJTWuoRVi1", // HIFU
      "3g1uBeizk5tdoTfMCd9Zws", // Radio Frequency & Ultrasound
      "7KkGHh8CFL5fvPMGE0DtDT", // Plasma Pen
    ],
    notes:
      "Assessment follows each qualification included in the package.",
  },
  {
    courseId: "QvKUBltIgrXFRC3xdLqqiK",
    url: "/courses/packages/permanent-makeup-career-package",
    title: "Permanent Makeup Career Package",
    methods: [],
    packageCourseIds: [
      "1graQlnkDnOBSl8sHkdf2L", // Access to Aesthetic Therapies
      "3C2fRaQRiwMjA3Inq6UHqK", // Micropigmentation
      "2D6Xd7ARoNFR8qaqRl7FM2", // Microblading
    ],
    notes:
      "Assessment follows each qualification included in the package.",
  },
  {
    courseId: "6SOzleUddSpZwSsJumB1Jy",
    url: "/courses/elearning/vtct-itec-level-4-award-in-principles-and-practice-of-aesthetic-therapies",
    title:
      "VTCT (ITEC) Level 4 Award in Principles and Practice of Aesthetic Therapies",
    methods: ["online_written_assignment"],
  },
  {
    courseId: "4UKqQaWjQNVo3rdlClbRQA",
    url: "/courses/elearning/vtct-itec-level-4-award-in-core-of-knowledge-for-laser-and-light-devices",
    title:
      "VTCT (ITEC) Level 4 Award in Core of Knowledge for Laser and Light Devices",
    methods: ["online_theory_exams"],
  },
  {
    courseId: "15jVSi503icvZCtPkVJlfp",
    url: "/courses/elearning/vtct-level-3-award-in-anatomical-and-physiological-knowledge-of-body",
    title:
      "Online VTCT Level 3 Award in Anatomical and Physiological Knowledge of Body Systems",
    methods: ["online_theory_exams"],
  },
];

export function getCourseAssessmentByUrl(
  url: string,
): CourseAssessmentConfig | undefined {
  const normalised = url.replace(/\/$/, "");
  return courseAssessments.find(
    (course) => course.url.replace(/\/$/, "") === normalised,
  );
}

export function getCourseAssessmentById(
  courseId: string,
): CourseAssessmentConfig | undefined {
  return courseAssessments.find((course) => course.courseId === courseId);
}

/** Resolve methods for display, including package unions. */
export function resolveCourseAssessment(
  config: CourseAssessmentConfig,
): CourseAssessmentConfig {
  if (!config.packageCourseIds?.length) {
    return config;
  }

  const methods = new Set<CourseAssessmentMethod>();
  const counts: Partial<Record<CourseAssessmentMethod, number>> = {
    ...(config.counts || {}),
  };

  for (const id of config.packageCourseIds) {
    const included = getCourseAssessmentById(id);
    if (!included) {
      continue;
    }
    for (const method of included.methods) {
      methods.add(method);
    }
    if (included.counts) {
      for (const [method, count] of Object.entries(included.counts) as Array<
        [CourseAssessmentMethod, number]
      >) {
        counts[method] = (counts[method] || 0) + count;
      }
    }
  }

  return {
    ...config,
    methods: COURSE_ASSESSMENT_METHODS.filter((method) => methods.has(method)),
    counts: Object.keys(counts).length ? counts : undefined,
  };
}
